import { api, extractData } from './api';

export type NotificationPreferences = {
    ranking_risk: boolean;
    streak_expiring: boolean;
    creator_reactions: boolean;
    content_approved: boolean;
};

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i += 1) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export function pushSupported(): boolean {
    return (
        typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window
    );
}

/** Register SW early; does not request notification permission. */
export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!pushSupported()) return null;
    try {
        const swUrl = `${process.env.PUBLIC_URL || ''}/sw.js`;
        return await navigator.serviceWorker.register(swUrl);
    } catch {
        return null;
    }
}

export async function fetchVapidPublicKey(): Promise<string | null> {
    try {
        const res = await api.get('/push/vapid-public-key');
        const data = extractData<{ public_key?: string }>(res);
        return data.public_key || null;
    } catch {
        return null;
    }
}

export async function fetchNotificationPreferences(): Promise<{
    preferences: NotificationPreferences;
    has_subscription: boolean;
    vapid_public_key: string | null;
}> {
    const res = await api.get('/notification-preferences');
    return extractData(res);
}

export async function updateNotificationPreferences(
    patch: Partial<NotificationPreferences>
): Promise<NotificationPreferences> {
    const res = await api.put('/notification-preferences', patch);
    const data = extractData<{ preferences: NotificationPreferences }>(res);
    return data.preferences;
}

export async function enablePushNotifications(): Promise<{
    preferences: NotificationPreferences;
}> {
    if (!pushSupported()) {
        throw new Error('Push notifications are not supported in this browser');
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
        throw new Error('Notification permission was not granted');
    }

    const registration = (await navigator.serviceWorker.getRegistration())
        || (await registerPushServiceWorker());
    if (!registration) {
        throw new Error('Service worker registration failed');
    }

    await navigator.serviceWorker.ready;

    const publicKey = await fetchVapidPublicKey();
    if (!publicKey) {
        throw new Error('Web Push is not configured on the server');
    }

    const existing = await registration.pushManager.getSubscription();
    const subscription = existing || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });

    const json = subscription.toJSON();
    const res = await api.post('/push-subscriptions', {
        endpoint: json.endpoint,
        keys: {
            p256dh: json.keys?.p256dh,
            auth: json.keys?.auth,
        },
    });

    const data = extractData<{ preferences: NotificationPreferences }>(res);
    return { preferences: data.preferences };
}

export async function disablePushNotifications(): Promise<void> {
    if (!pushSupported()) return;

    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;

    const endpoint = subscription.endpoint;
    try {
        await api.delete('/push-subscriptions', { data: { endpoint } });
    } finally {
        await subscription.unsubscribe();
    }
}

const firstQuizPushAskedKey = (userId: number) => `pq_first_quiz_push_asked:${userId}`;

/**
 * After a logged-in user's first completed quiz, request notification permission
 * and subscribe if granted. No-ops when already decided, unsupported, or asked before.
 */
export async function maybeEnablePushAfterFirstQuiz(userId: number): Promise<void> {
    if (!userId || !pushSupported()) return;
    if (typeof Notification === 'undefined') return;
    // Already granted or explicitly denied — do not prompt again.
    if (Notification.permission !== 'default') return;

    try {
        if (localStorage.getItem(firstQuizPushAskedKey(userId))) return;
    } catch {
        // private mode — continue once
    }

    try {
        localStorage.setItem(firstQuizPushAskedKey(userId), '1');
    } catch {
        // ignore
    }

    try {
        const registration = await navigator.serviceWorker.getRegistration();
        const existing = await registration?.pushManager.getSubscription();
        if (existing) return;
    } catch {
        // continue to request permission
    }

    try {
        await enablePushNotifications();
    } catch {
        // Denied or VAPID missing — silent; user can enable later in profile.
    }
}
